/**
 * Super-admin — Cross-tenant collaborator management.
 *
 * Visual idêntico ao SuperAdminClients: agrupa por empresa, cada tenant é
 * um card com avatar gradiente, plano, badge de uso vs limite e botão
 * "+ Colaborador" que pré-seleciona aquela empresa.
 *
 * Endpoints (bypass tenant scope on backend):
 *   GET    /super-admin/collaborators?tenant_id=&q=
 *   POST   /super-admin/collaborators
 *   PATCH  /super-admin/collaborators/{id}
 *   DELETE /super-admin/collaborators/{id}
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  RefreshCw,
  Save,
  X as XIcon,
  Building2,
  ChevronDown,
  ChevronRight,
  LayoutList,
  LayoutGrid,
  Users,
  Phone,
  Mail,
  UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { formatUSPhone } from "../lib/format";
import { useConfirm } from "../hooks/useConfirm";
import {
  tenantColor,
  initials,
  usageStatus,
  usageChipClass,
} from "../lib/tenantVisuals";
import { logError } from "../lib/logger";

const ROLE_LABELS = {
  admin: "Admin",
  supervisor: "Supervisor",
  collaborator: "Colaborador",
};

const ROLE_BADGE = {
  admin: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-500/40",
  supervisor: "bg-sky-500/20 text-sky-200 border-sky-500/40",
  collaborator: "bg-slate-700/60 text-slate-200 border-white/10",
};

export default function SuperAdminCollaborators() {
  const confirm = useConfirm();
  const [tenants, setTenants] = useState([]);
  const [filterTenant, setFilterTenant] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState("grouped");
  const [collapsed, setCollapsed] = useState(() => new Set());

  async function loadTenants() {
    try {
      const { data } = await api.get("/super-admin/tenants");
      setTenants(data || []);
    } catch (e) {
      logError("Failed to load tenants", e);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filterTenant) params.tenant_id = filterTenant;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/super-admin/collaborators", { params });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTenants();
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTenant]);

  async function doDelete(row) {
    const ok = await confirm({
      title: `Excluir "${row.name}"?`,
      description: `Esta ação é permanente. O colaborador será removido da empresa "${row.tenant_name || row.tenant_id}" e perde acesso ao sistema.`,
      destructive: true,
      actionLabel: "Excluir",
      requireText: row.name,
    });
    if (!ok) return;
    try {
      await api.delete(`/super-admin/collaborators/${row.id}`);
      toast.success(`"${row.name}" excluído`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  const tenantMeta = useMemo(() => {
    const m = new Map();
    for (const t of tenants) m.set(t.id, t);
    return m;
  }, [tenants]);

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const r of rows) {
      const key = r.tenant_id || "(sem-tenant)";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    }
    return Array.from(groups.entries())
      .map(([tid, list]) => ({
        tid,
        users: [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        meta: tenantMeta.get(tid),
        name:
          tenantMeta.get(tid)?.name ||
          list[0]?.tenant_name ||
          "(sem empresa)",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, tenantMeta]);

  const toggleGroup = (tid) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid);
      else next.add(tid);
      return next;
    });
  };

  return (
    <div className="space-y-5" data-testid="super-admin-collabs-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Colaboradores{" "}
            <span className="text-violet-300/80 font-normal">(todas as empresas)</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            <b>{rows.length}</b> usuário{rows.length !== 1 && "s"} em{" "}
            <b>{grouped.length}</b> empresa{grouped.length !== 1 && "s"} · veja,
            edite, crie e exclua colaboradores de qualquer tenant.
          </p>
        </div>
        <div className="flex gap-2">
          <ViewToggle view={view} setView={setView} />
          <button
            onClick={() => setCreating(true)}
            data-testid="new-collab-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm font-semibold text-white"
          >
            <Plus size={14} /> Novo colaborador
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-semibold text-slate-200"
            title="Recarregar"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr,280px] gap-2">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por nome ou e-mail… (Enter)"
            data-testid="collab-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-white/10 bg-white/5 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={filterTenant}
          onChange={(e) => setFilterTenant(e.target.value)}
          data-testid="collab-filter-tenant"
          className="px-3 py-2.5 rounded-lg border border-white/10 bg-slate-900 text-sm text-white"
        >
          <option value="">Todas as empresas</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400 bg-white/5 border border-white/10 rounded-xl">
          Carregando…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-slate-400 bg-white/5 border border-white/10 rounded-xl">
          Nenhum colaborador encontrado{filterTenant && " nesta empresa"}.
        </div>
      ) : view === "grouped" ? (
        <div className="space-y-3">
          {grouped.map((g) => (
            <TenantGroup
              key={g.tid}
              group={g}
              collapsed={collapsed.has(g.tid)}
              onToggle={() => toggleGroup(g.tid)}
              onEdit={setEditing}
              onDelete={doDelete}
              onAddInTenant={() => setCreating({ tenant_id: g.tid })}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="divide-y divide-white/5">
            {rows.map((r) => (
              <FlatRow
                key={r.id}
                row={r}
                onEdit={() => setEditing(r)}
                onDelete={() => doDelete(r)}
              />
            ))}
          </div>
        </div>
      )}

      {(creating || editing) && (
        <CollabModal
          row={editing}
          presetTenantId={typeof creating === "object" ? creating?.tenant_id : null}
          tenants={tenants}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div
      className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5"
      data-testid="collab-view-toggle"
    >
      <button
        onClick={() => setView("grouped")}
        data-testid="view-grouped"
        title="Agrupar por empresa"
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold transition ${
          view === "grouped"
            ? "bg-violet-600 text-white"
            : "text-slate-300 hover:text-white"
        }`}
      >
        <LayoutGrid size={13} /> Por empresa
      </button>
      <button
        onClick={() => setView("flat")}
        data-testid="view-flat"
        title="Lista plana"
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold transition ${
          view === "flat"
            ? "bg-violet-600 text-white"
            : "text-slate-300 hover:text-white"
        }`}
      >
        <LayoutList size={13} /> Lista
      </button>
    </div>
  );
}

function TenantGroup({ group, collapsed, onToggle, onEdit, onDelete, onAddInTenant }) {
  const c = tenantColor(group.tid);
  const meta = group.meta;
  const currentUsers =
    typeof meta?.user_count === "number" ? meta.user_count : group.users.length;
  const usage = usageStatus(currentUsers, meta?.limits?.max_collaborators);

  return (
    <section
      className="rounded-xl border border-white/10 bg-white/5 overflow-hidden"
      data-testid={`tenant-group-${group.tid}`}
    >
      <header
        className="relative px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-white/5 transition border-l-4"
        style={{ borderLeftColor: c.solid }}
        onClick={onToggle}
        data-testid={`tenant-group-header-${group.tid}`}
      >
        <button
          className="text-slate-400 hover:text-white"
          aria-label={collapsed ? "Expandir" : "Recolher"}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
        </button>
        <div
          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.bg} flex items-center justify-center text-white font-bold text-sm shadow-lg flex-shrink-0`}
        >
          {initials(group.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-white text-base truncate">{group.name}</h3>
            {meta?.plan_id && (
              <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200 border border-white/10">
                {meta.plan_id}
              </span>
            )}
            {meta?.status && meta.status !== "active" && (
              <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40">
                {meta.status}
              </span>
            )}
            <span
              className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${usageChipClass(usage.level)}`}
              title={`Limite do plano: ${meta?.limits?.max_collaborators || "ilimitado"}`}
              data-testid={`tenant-group-usage-${group.tid}`}
            >
              <UserCog size={10} /> {usage.text}
              {usage.level === "danger" && " · LIMITE!"}
              {usage.level === "warn" && " · perto do limite"}
            </span>
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
            <Building2 size={11} />
            <span className="truncate">{meta?.slug || group.tid}</span>
            <span className="text-slate-600">·</span>
            <span className="flex items-center gap-1">
              <Users size={11} /> {group.users.length} usuário
              {group.users.length !== 1 && "s"}
              {group.users.length !== currentUsers && (
                <span className="text-slate-500"> (filtrados de {currentUsers})</span>
              )}
            </span>
          </div>
          {usage.level !== "unlimited" && (
            <div className="mt-1.5 h-1 w-full max-w-[240px] rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${usage.percent}%`,
                  background:
                    usage.level === "danger"
                      ? "#ef4444"
                      : usage.level === "warn"
                        ? "#f59e0b"
                        : "#10b981",
                }}
              />
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddInTenant();
          }}
          data-testid={`tenant-group-add-${group.tid}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-white/10 bg-white/5 hover:bg-violet-600 hover:border-violet-500 transition text-slate-200 hover:text-white"
          title="Adicionar colaborador nesta empresa"
        >
          <Plus size={11} /> Colaborador
        </button>
      </header>

      {!collapsed && (
        <div className="divide-y divide-white/5 border-t border-white/5">
          {group.users.map((u) => (
            <CollabRow
              key={u.id}
              row={u}
              accent={c.solid}
              onEdit={() => onEdit(u)}
              onDelete={() => onDelete(u)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CollabRow({ row, accent, onEdit, onDelete }) {
  const isSuper = !!row.is_super_admin;
  return (
    <div
      className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-3 items-center hover:bg-white/5"
      data-testid={`collab-row-${row.id}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div
          className="w-1 self-stretch rounded-full flex-shrink-0"
          style={{ background: accent }}
          aria-hidden
        />
        <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-slate-200 flex items-center justify-center flex-shrink-0 text-xs font-bold">
          {initials(row.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-white truncate flex items-center gap-1.5 flex-wrap">
            {row.name}
            <span
              className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${ROLE_BADGE[row.role] || ROLE_BADGE.collaborator}`}
            >
              {ROLE_LABELS[row.role] || row.role}
            </span>
            {isSuper && (
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30 font-bold">
                SUPER
              </span>
            )}
            {row.status && row.status !== "ativo" && (
              <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300 border border-white/10">
                {row.status}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-400 flex items-center gap-3 mt-0.5 flex-wrap">
            <span className="flex items-center gap-1 truncate">
              <Mail size={10} /> {row.email}
            </span>
            {row.phone && (
              <span className="flex items-center gap-1">
                <Phone size={10} /> {row.phone}
              </span>
            )}
            {row.valor_por_dia > 0 && (
              <span className="flex items-center gap-1 text-emerald-300 font-semibold">
                ${Number(row.valor_por_dia).toFixed(2)}/dia
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-1.5">
        <button
          onClick={onEdit}
          data-testid={`collab-edit-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
        >
          <Pencil size={12} /> Editar
        </button>
        <button
          onClick={onDelete}
          disabled={isSuper}
          data-testid={`collab-delete-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25 disabled:opacity-30 disabled:cursor-not-allowed"
          title={isSuper ? "Não é possível excluir um super-admin" : "Excluir"}
        >
          <Trash2 size={12} /> Excluir
        </button>
      </div>
    </div>
  );
}

function FlatRow({ row, onEdit, onDelete }) {
  const c = tenantColor(row.tenant_id);
  const isSuper = !!row.is_super_admin;
  return (
    <div
      className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[260px,1fr,auto] gap-3 items-center hover:bg-white/5"
      data-testid={`collab-row-${row.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={`w-7 h-7 rounded-lg bg-gradient-to-br ${c.bg} flex items-center justify-center text-white font-bold text-[11px] flex-shrink-0 shadow`}
          title={row.tenant_name || row.tenant_id}
        >
          {initials(row.tenant_name || "")}
        </div>
        <div
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${c.chip} text-xs font-semibold truncate`}
        >
          <Building2 size={11} className="flex-shrink-0" />
          <span className="truncate">{row.tenant_name || row.tenant_id}</span>
        </div>
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-white truncate flex items-center gap-2 flex-wrap">
          {row.name}
          <span
            className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${ROLE_BADGE[row.role] || ROLE_BADGE.collaborator}`}
          >
            {ROLE_LABELS[row.role] || row.role}
          </span>
          {isSuper && (
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30 font-bold">
              SUPER
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400 truncate">{row.email}</div>
      </div>
      <div className="flex justify-end gap-1.5">
        <button
          onClick={onEdit}
          data-testid={`collab-edit-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
        >
          <Pencil size={12} /> Editar
        </button>
        <button
          onClick={onDelete}
          disabled={isSuper}
          data-testid={`collab-delete-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Trash2 size={12} /> Excluir
        </button>
      </div>
    </div>
  );
}

function CollabModal({ row, presetTenantId, tenants, onClose, onSaved }) {
  const isNew = !row;
  const [form, setForm] = useState({
    name: row?.name || "",
    email: row?.email || "",
    role: row?.role || "collaborator",
    phone: row?.phone || "",
    valor_por_dia: row?.valor_por_dia ?? 0,
    status: row?.status || "ativo",
    tenant_id: row?.tenant_id || presetTenantId || tenants[0]?.id || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const tenantInfo = tenants.find((t) => t.id === form.tenant_id);
  const c = tenantColor(form.tenant_id);

  async function save() {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nome e e-mail são obrigatórios");
      return;
    }
    if (isNew && !form.tenant_id) {
      toast.error("Selecione uma empresa");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const body = { ...form };
        if (!body.password) {
          // Gera senha randômica em vez de usar default previsível
          body.password = Math.random().toString(36).slice(2, 10) + "A1!";
        }
        await api.post("/super-admin/collaborators", body);
        toast.success(`Colaborador criado em ${tenantInfo?.name || "empresa"}. Senha: ${body.password}`, { duration: 15000 });
      } else {
        const body = { ...form };
        if (!body.password) delete body.password;
        await api.patch(`/super-admin/collaborators/${row.id}`, body);
        toast.success("Colaborador atualizado");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full text-white max-h-[90vh] overflow-y-auto"
        data-testid="collab-modal"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-wider text-violet-300 font-semibold">
              {isNew ? "Novo colaborador" : "Editar colaborador"}
            </div>
            <h2 className="text-lg font-bold text-white">
              {form.name || "(sem nome)"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div
          className="mx-5 mt-5 rounded-xl border p-3 flex items-center gap-3"
          style={{ borderColor: c.solid + "55", background: c.solid + "12" }}
        >
          <div
            className={`w-10 h-10 rounded-lg bg-gradient-to-br ${c.bg} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
          >
            {initials(tenantInfo?.name || "?")}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              {isNew ? "Será adicionado em" : "Colaborador pertence a"}
            </div>
            <select
              value={form.tenant_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, tenant_id: e.target.value }))
              }
              data-testid="collab-modal-tenant"
              className="w-full bg-transparent text-white font-bold outline-none border-b border-white/10 focus:border-violet-500 transition"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id} className="bg-slate-900">
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <Field label="Nome">
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="collab-modal-name"
              className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="E-mail">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="collab-modal-email"
              className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Papel">
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                data-testid="collab-modal-role"
                className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
              >
                <option value="collaborator">Colaborador</option>
                <option value="supervisor">Supervisor</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="pending">Pendente</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone">
              <input
                value={form.phone}
                type="tel"
                inputMode="tel"
                maxLength={14}
                placeholder="(555) 123-4567"
                onChange={(e) => setForm((f) => ({ ...f, phone: formatUSPhone(e.target.value) }))}
                className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Valor por dia (R$)">
              <input
                type="number"
                step="0.01"
                value={form.valor_por_dia}
                onChange={(e) =>
                  setForm((f) => ({ ...f, valor_por_dia: e.target.value }))
                }
                className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <Field
            label={isNew ? "Senha inicial" : "Nova senha (deixe vazio p/ manter)"}
          >
            <input
              type="text"
              value={form.password}
              onChange={(e) =>
                setForm((f) => ({ ...f, password: e.target.value }))
              }
              placeholder={isNew ? "colab123" : "••••••"}
              data-testid="collab-modal-password"
              className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm font-mono"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10 sticky bottom-0 bg-slate-900">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="collab-modal-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm font-semibold disabled:opacity-50"
          >
            <Save size={14} /> {isNew ? "Criar" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-300 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
